import React, { useState, useEffect } from "react";
import { useTheme } from "./ThemeContext";

/**
 * Simplified Terms and Conditions implementation
 * - No storage of terms acceptance
 * - Terms are shown but acceptance is implicit
 * - Focus on displaying terms with minimal code
 */

// Core Terms and Conditions Content
export const TermsContent = ({ isModal = false, onClose }) => {
   const { theme } = useTheme();
   const isLightMode = theme === "light";

   // Define styles based on theme and modal state
   const styles = {
      container: {
         width: "100%",
         maxWidth: isModal ? "100%" : "800px",
         margin: isModal ? "0" : "0 auto",
         backgroundColor: isLightMode
            ? "var(--bg-elevated, #fff)"
            : "rgba(34, 34, 34, 0.98)",
         borderRadius: "0.5rem",
         border: `1px solid ${
            isLightMode ? "var(--border-color, #ddd)" : "#333"
         }`,
         boxShadow: isLightMode
            ? "0 4px 8px rgba(0, 0, 0, 0.1)"
            : "0 4px 8px rgba(0, 0, 0, 0.3)",
         padding: isModal ? "1rem" : "2rem",
         marginBottom: "1.5rem",
      },
      header: {
         color: isLightMode ? "var(--text-primary, #333)" : "#fff",
         fontWeight: "700",
         fontSize: "1.5rem",
         marginBottom: "1rem",
         textAlign: "center",
      },
      content: {
         maxHeight: "400px",
         overflowY: "auto",
         padding: "1rem",
         marginBottom: "1.5rem",
         backgroundColor: isLightMode
            ? "var(--bg-tertiary, #f8f9fa)"
            : "rgba(40, 40, 40, 0.6)",
         borderRadius: "0.375rem",
         border: `1px solid ${
            isLightMode ? "var(--border-color, #ddd)" : "#444"
         }`,
         color: isLightMode ? "var(--text-primary, #333)" : "#ddd",
         fontSize: "0.9rem",
         lineHeight: "1.6",
      },
      sectionTitle: {
         fontWeight: "700",
         marginTop: "1rem",
         marginBottom: "0.5rem",
         color: isLightMode ? "var(--text-primary, #333)" : "#fff",
      },
      paragraph: {
         marginBottom: "0.75rem",
      },
      buttonContainer: {
         display: "flex",
         justifyContent: "center",
         marginTop: "1rem",
      },
      closeButton: {
         backgroundColor: "var(--accent-primary, #10B981)",
         color: "white",
         padding: "0.75rem 1.5rem",
         borderRadius: "0.375rem",
         border: "none",
         fontSize: "0.95rem",
         fontWeight: "500",
         cursor: "pointer",
         transition: "background-color 0.2s ease",
      },
   };

   return (
      <div style={styles.container}>
         {isModal && (
            <div className="d-flex justify-content-between align-items-center mb-3">
               <h2 style={styles.header}>Terms and Conditions</h2>
               {onClose && (
                  <button
                     className="btn-close"
                     aria-label="Close"
                     onClick={onClose}
                     style={{
                        color: isLightMode
                           ? "var(--text-primary, #333)"
                           : "#fff",
                     }}
                  ></button>
               )}
            </div>
         )}

         {!isModal && <h2 style={styles.header}>Terms and Conditions</h2>}

         <div style={styles.content}>
            <h3 style={styles.sectionTitle}>
               Terms and Conditions for the Use of Pageshift Entertainment GmbH
            </h3>

            <h4 style={styles.sectionTitle}>1. Scope of Application</h4>
            <p style={styles.paragraph}>
               These Terms and Conditions ("T&C") apply to all agreements
               between Pageshift Entertainment GmbH ("Provider") and users
               ("Customers") regarding the use of the AI audiobook generation
               platform accessible at https://pageshift.ai/.
            </p>

            <h4 style={styles.sectionTitle}>2. Services Provided</h4>
            <p style={styles.paragraph}>
               (1) Pageshift Entertainment GmbH offers users the ability to
               generate and store personalized audiobooks through an AI-powered
               platform.
            </p>
            <p style={styles.paragraph}>
               (2) Usage of the platform is initially free of charge. Users must
               create an account by registering with valid login credentials to
               access and build their audiobook library.
            </p>
            <p style={styles.paragraph}>
               (3) The scope of the free services may be expanded, limited, or
               changed in the future, with reasonable notice to users.
            </p>

            <h4 style={styles.sectionTitle}>3. User Obligations</h4>
            <p style={styles.paragraph}>
               (1) Users are responsible for ensuring that any content they
               submit (e.g., text for audiobook generation) does not infringe
               upon third-party rights, particularly intellectual property
               rights, and complies with applicable laws.
            </p>
            <p style={styles.paragraph}>
               (2) Users must not misuse the service for illegal, offensive, or
               harmful purposes.
            </p>

            <h4 style={styles.sectionTitle}>4. Intellectual Property</h4>
            <p style={styles.paragraph}>
               (1) Pageshift Entertainment GmbH retains all intellectual
               property rights related to the platform, the software, and the
               underlying AI technologies.
            </p>
            <p style={styles.paragraph}>
               (2) By uploading or submitting content to the platform, users
               irrevocably assign to Pageshift Entertainment GmbH all rights,
               title, and interest in and to the content, including any
               intellectual property rights, to the fullest extent permitted by
               applicable law. Pageshift Entertainment GmbH shall have the
               exclusive, worldwide, perpetual, and unrestricted right to use,
               modify, reproduce, distribute, commercialize, and otherwise
               exploit such content in any form and for any purpose.
            </p>
            <p style={styles.paragraph}>
               (3) Users confirm that they have the legal right to transfer such
               rights and that the content is free from third-party claims.
            </p>

            <h4 style={styles.sectionTitle}>5. Use of Data for AI Training</h4>
            <p style={styles.paragraph}>
               (1) Users acknowledge and agree that all content, inputs,
               interactions, and data submitted to or generated through the
               platform may be used by Pageshift Entertainment GmbH for the
               purpose of training, improving, and developing its AI models and
               related technologies.
            </p>
            <p style={styles.paragraph}>
               (2) This includes but is not limited to any text submitted, usage
               patterns, selections, or generated audiobook outputs.
            </p>
            <p style={styles.paragraph}>
               (3) All such use shall be handled in accordance with applicable
               data and intellectual property laws.
            </p>

            <h4 style={styles.sectionTitle}>6. Pricing and Future Changes</h4>
            <p style={styles.paragraph}>
               (1) Currently, usage of Pageshift Entertainment GmbH's platform
               is free.
            </p>
            <p style={styles.paragraph}>
               (2) Pageshift Entertainment GmbH reserves the right to introduce
               paid features or premium offerings in the future. If any changes
               occur, users will be informed in advance and given the option to
               agree to the new pricing models.
            </p>

            <h4 style={styles.sectionTitle}>7. Disclaimer of Liability</h4>
            <p style={styles.paragraph}>
               (1) Pageshift Entertainment GmbH provides the service "as is" and
               makes no representations or warranties of any kind, express or
               implied, regarding the functionality, accuracy, availability, or
               reliability of the platform or the generated content.
            </p>
            <p style={styles.paragraph}>
               (2) To the fullest extent permitted by applicable law, Pageshift
               Entertainment GmbH shall not be liable for any damages, including
               but not limited to direct, indirect, incidental, consequential,
               or punitive damages, arising out of or related to the use of, or
               inability to use, the platform or generated content.
            </p>
            <p style={styles.paragraph}>
               (3) Nothing in these T&C shall limit or exclude liability for
               damages resulting from intentional misconduct or for injury to
               life, body, or health where such liability cannot be excluded by
               law.
            </p>

            <h4 style={styles.sectionTitle}>8. Warranty Disclaimer</h4>
            <p style={styles.paragraph}>
               Pageshift Entertainment GmbH does not guarantee that the platform
               will be available at all times without interruption, nor that the
               generated audiobooks will always meet user expectations. Minor
               technical inaccuracies or errors may occur due to the nature of
               AI processing.
            </p>

            <h4 style={styles.sectionTitle}>9. Termination</h4>
            <p style={styles.paragraph}>
               (1) Users may terminate their use of the service and request the
               deletion of their accounts at any time by contacting Pageshift
               Entertainment GmbH's user support.
            </p>
            <p style={styles.paragraph}>
               (2) Pageshift Entertainment GmbH reserves the right to terminate
               accounts in cases of violation of these T&C or misuse of the
               platform.
            </p>

            <h4 style={styles.sectionTitle}>10. Data Handling</h4>
            <p style={styles.paragraph}>
               Pageshift Entertainment GmbH collects and processes personal data
               and platform usage data as part of providing and improving its
               services, including for AI training purposes.
            </p>

            <h4 style={styles.sectionTitle}>11. Final Provisions</h4>
            <p style={styles.paragraph}>
               (1) These T&C are governed by the applicable laws of the
               jurisdiction where the Provider is based.
            </p>
            <p style={styles.paragraph}>
               (2) Should individual provisions of these T&C be invalid, the
               validity of the remaining provisions shall remain unaffected.
            </p>
            <p style={styles.paragraph}>
               (3) The place of jurisdiction shall be determined by applicable
               law.
            </p>
         </div>

         {isModal && (
            <div style={styles.buttonContainer}>
               <button
                  style={styles.closeButton}
                  onClick={onClose}
                  onMouseOver={(e) =>
                     (e.currentTarget.style.backgroundColor = "#0e9f6e")
                  }
                  onMouseOut={(e) =>
                     (e.currentTarget.style.backgroundColor =
                        "var(--accent-primary, #10B981)")
                  }
               >
                  Close
               </button>
            </div>
         )}
      </div>
   );
};

// Simple Terms Modal (no acceptance tracking)
export const TermsModal = ({ show, onClose }) => {
   const { theme } = useTheme();
   const isLightMode = theme === "light";
   const [isVisible, setIsVisible] = useState(false);

   // Animation effect when showing/hiding modal
   useEffect(() => {
      if (show) {
         document.body.style.overflow = "hidden"; // Prevent scrolling behind modal
         setTimeout(() => setIsVisible(true), 50);
      } else {
         setIsVisible(false);
         setTimeout(() => (document.body.style.overflow = ""), 300);
      }
   }, [show]);

   if (!show) return null;

   const handleClose = () => {
      setIsVisible(false);
      setTimeout(() => {
         if (typeof onClose === "function") {
            onClose();
         }
      }, 300);
   };

   // Add custom CSS styles directly in the component
   const modalStyles = `
    .modal-backdrop {
      opacity: 0;
      transition: opacity 0.3s ease;
    }
    
    .modal-backdrop.show {
      opacity: 1;
    }
    
    .modal-content {
      transform: translateY(20px);
      transition: transform 0.3s ease;
    }
    
    .modal-content.show {
      transform: translateY(0);
    }
  `;

   return (
      <>
         <style>{modalStyles}</style>
         <div
            className={`modal-backdrop ${isVisible ? "show" : ""}`}
            style={{
               position: "fixed",
               top: 0,
               left: 0,
               right: 0,
               bottom: 0,
               backgroundColor: "rgba(0, 0, 0, 0.5)",
               display: "flex",
               alignItems: "center",
               justifyContent: "center",
               zIndex: 1050,
               opacity: isVisible ? 1 : 0,
               transition: "opacity 0.3s ease",
               padding: "1rem",
            }}
            onClick={handleClose}
         >
            <div
               className={`modal-content ${isVisible ? "show" : ""}`}
               style={{
                  backgroundColor: isLightMode
                     ? "var(--bg-elevated, #fff)"
                     : "rgba(34, 34, 34, 0.98)",
                  borderRadius: "0.5rem",
                  width: "100%",
                  maxWidth: "700px",
                  maxHeight: "90vh",
                  overflow: "hidden",
                  boxShadow: "0 5px 15px rgba(0, 0, 0, 0.5)",
                  transform: isVisible ? "translateY(0)" : "translateY(20px)",
                  transition: "transform 0.3s ease",
               }}
               onClick={(e) => e.stopPropagation()}
            >
               <TermsContent isModal={true} onClose={handleClose} />
            </div>
         </div>
      </>
   );
};

// Standalone Terms Page
export const TermsPage = () => {
   const { theme } = useTheme();
   const isLightMode = theme === "light";

   return (
      <div className="container py-4">
         <div className="row justify-content-center">
            <div className="col-lg-10">
               <div
                  style={{
                     backgroundColor: isLightMode
                        ? "var(--bg-elevated, #fff)"
                        : "rgba(34, 34, 34, 0.98)",
                     borderRadius: "0.5rem",
                     border: `1px solid ${
                        isLightMode ? "var(--border-color, #ddd)" : "#333"
                     }`,
                     padding: "2rem",
                     boxShadow: isLightMode
                        ? "0 4px 8px rgba(0, 0, 0, 0.1)"
                        : "0 4px 8px rgba(0, 0, 0, 0.3)",
                     marginBottom: "2rem",
                  }}
               >
                  <div className="mb-4">
                     <h1
                        style={{
                           color: isLightMode
                              ? "var(--text-primary, #333)"
                              : "#fff",
                           fontWeight: "700",
                           fontSize: "2rem",
                           marginBottom: "1rem",
                           textAlign: "center",
                        }}
                     >
                        Terms and Conditions
                     </h1>
                     <p
                        style={{
                           color: isLightMode
                              ? "var(--text-muted, #6B7280)"
                              : "#aaa",
                           textAlign: "center",
                        }}
                     >
                        Last updated: April 30, 2025
                     </p>
                  </div>

                  <div className="mb-4">
                     <p
                        style={{
                           color: isLightMode
                              ? "var(--text-secondary, #4B5563)"
                              : "#ccc",
                           lineHeight: "1.6",
                           fontSize: "1.1rem",
                        }}
                     >
                        These Terms and Conditions govern your use of Pageshift
                        Entertainment GmbH's services. By accessing or using our
                        platform, you agree to be bound by these terms. Please
                        read them carefully before using our services.
                     </p>
                  </div>

                  <TermsContent />

                  <div className="mt-5">
                     <p
                        style={{
                           color: isLightMode
                              ? "var(--text-secondary, #4B5563)"
                              : "#ccc",
                           fontSize: "0.9rem",
                           fontStyle: "italic",
                           textAlign: "center",
                        }}
                     >
                        If you have any questions about these Terms and
                        Conditions, please contact us at: support@pageshift.ai
                     </p>
                  </div>
               </div>
            </div>
         </div>
      </div>
   );
};

// Footer Component with Terms Link
export const Footer = ({ navigateToView }) => {
   const { theme } = useTheme();
   const isLightMode = theme === "light";

   return (
      <footer
         className="mt-5 py-4"
         style={{
            backgroundColor: isLightMode
               ? "var(--bg-tertiary, #f8f9fa)"
               : "rgba(20, 20, 20, 0.95)",
            borderTop: `1px solid ${
               isLightMode ? "var(--border-color, #ddd)" : "#333"
            }`,
         }}
      >
         <div className="container">
            <div className="row">
               <div className="col-md-6">
                  <p
                     className="mb-0"
                     style={{
                        color: isLightMode
                           ? "var(--text-secondary, #4B5563)"
                           : "#aaa",
                        fontSize: "0.9rem",
                     }}
                  >
                     © 2025 Pageshift Entertainment GmbH. All rights reserved.
                  </p>
               </div>
               <div className="col-md-6 text-md-end">
                  <a
                     href="#"
                     onClick={(e) => {
                        e.preventDefault();
                        if (navigateToView) {
                           navigateToView("terms");
                        }
                     }}
                     style={{
                        color: isLightMode
                           ? "var(--text-secondary, #4B5563)"
                           : "#aaa",
                        textDecoration: "none",
                        fontSize: "0.9rem",
                        marginLeft: "1.5rem",
                     }}
                  >
                     Terms & Conditions
                  </a>
                  <a
                     href="mailto:support@pageshift.ai"
                     style={{
                        color: isLightMode
                           ? "var(--text-secondary, #4B5563)"
                           : "#aaa",
                        textDecoration: "none",
                        fontSize: "0.9rem",
                        marginLeft: "1.5rem",
                     }}
                  >
                     Contact
                  </a>
               </div>
            </div>
         </div>
      </footer>
   );
};

// Simple Terms Link Component for Signup Page
export const TermsLink = ({ setShowTermsModal }) => {
   const { theme } = useTheme();
   const isLightMode = theme === "light";

   return (
      <div className="mb-4">
         <p
            style={{
               color: isLightMode ? "var(--text-secondary, #4B5563)" : "#bbb",
               fontSize: "0.9rem",
               textAlign: "center",
            }}
         >
            By signing up, you agree to our{" "}
            <a
               href="#"
               onClick={(e) => {
                  e.preventDefault();
                  setShowTermsModal(true);
               }}
               style={{
                  color: "var(--accent-primary, #10B981)",
                  textDecoration: "none",
               }}
            >
               Terms and Conditions
            </a>
         </p>
      </div>
   );
};
